import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DashboardOutlined,
  LineChartOutlined,
  AlertOutlined,
  SettingOutlined,
  BellOutlined,
  UserOutlined,
  SunOutlined,
  MoonOutlined,
  BookOutlined,
  AppstoreOutlined,
  RobotOutlined,
  BulbOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  InboxOutlined,
  ToolOutlined,
  ApiOutlined,
  QuestionCircleOutlined,
  LogoutOutlined,
  HomeOutlined,
  DatabaseOutlined,
  BranchesOutlined,
  TagOutlined,
  TeamOutlined,
  BugOutlined,
  GlobalOutlined,
  DollarOutlined,
  CalendarOutlined,
  SwapOutlined,
  AmazonOutlined,
  ShopOutlined,
  ThunderboltOutlined,
  MobileOutlined,
  StarOutlined,
  HeartOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { ProLayout } from '@ant-design/pro-components';
import {
  Badge,
  Dropdown,
  Space,
  Switch,
  Avatar,
  Typography,
  Tooltip,
  Button,
  message,
  Drawer,
  List,
  Tag,
  Empty,
  Spin,
} from 'antd';
import type { MenuProps } from 'antd';
import { useState } from 'react';
import { useThemeStore } from '../stores/themeStore';
import { useAuthStore } from '../stores/authStore';
import { apiClient } from '../services/api';

const { Text } = Typography;

/**
 * Menu route configuration for the sidebar navigation
 * All routes are prefixed with /app for protected app routes
 */
const menuRoutes = {
  routes: [
    {
      path: '/app/dashboard',
      name: 'Dashboard',
      icon: <DashboardOutlined />,
    },
    {
      path: '/app/analytics',
      name: 'Analytics',
      icon: <LineChartOutlined />,
      routes: [
        { path: '/app/analytics/revenue', name: 'Revenue', icon: <DollarOutlined /> },
        { path: '/app/analytics/products', name: 'Products', icon: <ShoppingOutlined /> },
        { path: '/app/analytics/customers', name: 'Customers', icon: <TeamOutlined /> },
        { path: '/app/analytics/forecast', name: 'Forecast', icon: <LineChartOutlined /> },
        {
          path: '/app/analytics/marketing',
          name: 'Marketing Attribution',
          icon: <ThunderboltOutlined />,
        },
        { path: '/app/analytics/website', name: 'Website Performance', icon: <GlobalOutlined /> },
        { path: '/app/analytics/profitability', name: 'Profitability', icon: <DollarOutlined /> },
        { path: '/app/analytics/wbr', name: 'Weekly Business Review', icon: <CalendarOutlined /> },
      ],
    },
    {
      path: '/app/operations',
      name: 'Operations',
      icon: <ToolOutlined />,
      routes: [
        { path: '/app/operations/orders', name: 'Order Lifecycle', icon: <ShoppingCartOutlined /> },
        { path: '/app/operations/inventory', name: 'Inventory Health', icon: <InboxOutlined /> },
      ],
    },
    {
      path: '/app/source-dashboards',
      name: 'Source Dashboards',
      icon: <SwapOutlined />,
      routes: [
        { path: '/app/sources', name: 'All Sources', icon: <AppstoreOutlined /> },
        { path: '/app/sources/amazon', name: 'Amazon', icon: <AmazonOutlined /> },
        { path: '/app/sources/ebay', name: 'eBay', icon: <ShopOutlined /> },
        { path: '/app/sources/walmart', name: 'Walmart', icon: <ShopOutlined /> },
        { path: '/app/sources/shopee', name: 'Shopee', icon: <ShopOutlined /> },
        { path: '/app/sources/etsy', name: 'Etsy', icon: <ShopOutlined /> },
        { path: '/app/sources/web_direct', name: 'Web Direct', icon: <GlobalOutlined /> },
        { path: '/app/sources/mobile_app', name: 'Mobile App', icon: <MobileOutlined /> },
        { path: '/app/sources/erp', name: 'ERP', icon: <DatabaseOutlined /> },
        { path: '/app/sources/comparison', name: 'Cross-Source', icon: <SwapOutlined /> },
        { path: '/app/sources/insights', name: 'Source Insights', icon: <BulbOutlined /> },
      ],
    },
    {
      path: '/app/data',
      name: 'Data Explorer',
      icon: <DatabaseOutlined />,
      routes: [
        { path: '/app/data/overview', name: 'Overview', icon: <AppstoreOutlined /> },
        { path: '/app/data/orders', name: 'Orders', icon: <ShoppingCartOutlined /> },
        { path: '/app/data/products', name: 'Products', icon: <TagOutlined /> },
        { path: '/app/data/customers', name: 'Customers', icon: <TeamOutlined /> },
        { path: '/app/data/inventory', name: 'Inventory', icon: <InboxOutlined /> },
        { path: '/app/data/channels', name: 'Channels', icon: <BranchesOutlined /> },
        { path: '/app/data/insights', name: 'AI Insights', icon: <BulbOutlined /> },
        { path: '/app/data/alerts', name: 'Alerts', icon: <AlertOutlined /> },
        { path: '/app/data/errors', name: 'Error Logs', icon: <BugOutlined /> },
        { path: '/app/data/sales-velocity', name: 'Sales Velocity', icon: <BarChartOutlined /> },
        { path: '/app/data/product-reviews', name: 'Product Reviews', icon: <StarOutlined /> },
        { path: '/app/data/customer-health', name: 'Customer Health', icon: <HeartOutlined /> },
      ],
    },
    {
      path: '/app/ask-ai-section',
      name: 'Ask AI',
      icon: <RobotOutlined />,
      routes: [
        { path: '/app/ask-ai', name: 'Ask AI Chat', icon: <RobotOutlined /> },
        { path: '/app/ai-monitoring', name: 'AI Monitoring', icon: <BarChartOutlined /> },
        { path: '/app/ai-monitoring/requests', name: 'Requests', icon: <ApiOutlined /> },
        { path: '/app/ai-monitoring/chats', name: 'Chats', icon: <TeamOutlined /> },
        { path: '/app/ai-monitoring/usage', name: 'Token Usage', icon: <ThunderboltOutlined /> },
        { path: '/app/ai-monitoring/errors', name: 'Errors', icon: <BugOutlined /> },
      ],
    },
    {
      path: '/app/recommendations',
      name: 'Recommendations',
      icon: <BulbOutlined />,
    },
    {
      path: '/app/kpis',
      name: 'KPI Catalog',
      icon: <BookOutlined />,
    },
    {
      path: '/app/templates',
      name: 'Templates',
      icon: <AppstoreOutlined />,
    },
    {
      path: '/app/dashboards',
      name: 'My Dashboards',
      icon: <DashboardOutlined />,
    },
    {
      path: '/app/alerts',
      name: 'Alerts',
      icon: <AlertOutlined />,
    },
    {
      path: '/app/connectors',
      name: 'Connectors',
      icon: <ApiOutlined />,
    },
    {
      path: '/app/etl-history',
      name: 'ETL History',
      icon: <DatabaseOutlined />,
    },
    {
      path: '/app/settings',
      name: 'Settings',
      icon: <SettingOutlined />,
      routes: [
        { path: '/app/settings/notifications', name: 'Notifications', icon: <BellOutlined /> },
        { path: '/app/settings/theme', name: 'Appearance', icon: <SunOutlined /> },
        { path: '/app/settings/ai', name: 'AI Configuration', icon: <RobotOutlined /> },
      ],
    },
    {
      path: '/help',
      name: 'Help & Support',
      icon: <QuestionCircleOutlined />,
      routes: [
        { path: '/help/overview', name: 'Help Center', icon: <HomeOutlined /> },
        { path: '/help/getting-started', name: 'Getting Started', icon: <BookOutlined /> },
        { path: '/help/demo', name: 'Demo Guide', icon: <BulbOutlined /> },
        { path: '/help/screens', name: 'Screen Guides', icon: <AppstoreOutlined /> },
        { path: '/help/features', name: 'Features', icon: <RobotOutlined /> },
        { path: '/help/kpis', name: 'KPI Reference', icon: <LineChartOutlined /> },
        { path: '/help/faq', name: 'FAQ', icon: <QuestionCircleOutlined /> },
      ],
    },
  ],
};

/**
 * Main application layout component
 * Provides the dashboard shell with sidebar navigation, header, and content area
 *
 * Features:
 * - Sidebar navigation with collapsible menu
 * - Theme toggle (light/dark mode)
 * - User dropdown with profile and logout
 * - Help button for quick access to documentation
 * - Notifications badge
 */
/**
 * Notification data structure from API
 */
interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'critical';
  time: string;
  read: boolean;
}

/**
 * Fetch notifications from API
 */
async function fetchNotifications(): Promise<Notification[]> {
  const response = await apiClient.get<Notification[]>('/data/notifications');
  return response.data;
}

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, setMode, sidebarCollapsed, setSidebarCollapsed } = useThemeStore();
  const { user, logout } = useAuthStore();
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Fetch real notifications from API
  const { data: notifications = [], isLoading: notificationsLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: fetchNotifications,
    refetchInterval: 60000, // Refetch every minute
  });

  const isDark = mode === 'DARK';
  const unreadCount = notifications.filter((n) => !n.read).length;

  /**
   * Handle user logout
   * Clears session and redirects to home
   */
  const handleLogout = () => {
    logout();
    message.success('You have been logged out');
    navigate('/');
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div style={{ padding: '4px 0' }}>
          <Text strong style={{ display: 'block' }}>
            {user?.name || 'Demo User'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {user?.email}
          </Text>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' },
    {
      key: 'preferences',
      label: 'Preferences',
      icon: <SettingOutlined />,
      onClick: () => navigate('/app/settings/theme'),
    },
    { type: 'divider' },
    {
      key: 'home',
      label: 'Back to Home',
      icon: <HomeOutlined />,
      onClick: () => navigate('/'),
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  return (
    <ProLayout
      title="Growth Intelligence"
      logo="/logo.svg"
      layout="mix"
      fixSiderbar
      fixedHeader
      route={menuRoutes}
      location={{ pathname: location.pathname }}
      collapsed={sidebarCollapsed}
      onCollapse={setSidebarCollapsed}
      menuItemRender={(item, dom) => (
        <div onClick={() => item.path && navigate(item.path)}>{dom}</div>
      )}
      headerTitleRender={(logo, title) => (
        <Space>
          {logo}
          {!sidebarCollapsed && title}
        </Space>
      )}
      actionsRender={() => [
        <Tooltip key="help" title="Help & Support">
          <Button
            type="text"
            icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
            onClick={() => navigate('/help')}
          />
        </Tooltip>,
        <Switch
          key="theme-switch"
          checkedChildren={<MoonOutlined />}
          unCheckedChildren={<SunOutlined />}
          checked={isDark}
          onChange={(checked) => setMode(checked ? 'DARK' : 'LIGHT')}
        />,
        <Tooltip key="notifications" title="Notifications">
          <Button
            type="text"
            onClick={() => setNotificationsOpen(true)}
            style={{ padding: '4px 8px' }}
          >
            <Badge count={unreadCount} size="small">
              <BellOutlined style={{ fontSize: 18 }} />
            </Badge>
          </Button>
        </Tooltip>,
        <Dropdown key="user" menu={{ items: userMenuItems }} placement="bottomRight">
          <Space style={{ cursor: 'pointer' }}>
            <Avatar
              size="small"
              icon={<UserOutlined />}
              style={{
                background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
              }}
            >
              {user?.name?.charAt(0) || 'D'}
            </Avatar>
            <Text className="hide-on-mobile">{user?.name || 'Demo User'}</Text>
          </Space>
        </Dropdown>,
      ]}
      menuFooterRender={(props) => {
        if (props?.collapsed) return undefined;
        return (
          <div style={{ padding: '16px', textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Growth Intelligence v1.0
            </Text>
          </div>
        );
      }}
    >
      <Outlet />

      {/* Notifications Drawer */}
      <Drawer
        title={
          <Space>
            <BellOutlined />
            <span>Notifications</span>
            {unreadCount > 0 && <Tag color="blue">{unreadCount} unread</Tag>}
          </Space>
        }
        placement="right"
        width={400}
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      >
        {notificationsLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : notifications.length === 0 ? (
          <Empty description="No notifications" />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={notifications}
            renderItem={(notification) => {
              const typeColors = {
                info: 'blue',
                warning: 'orange',
                success: 'green',
                critical: 'red',
              };
              return (
                <List.Item
                  style={{
                    background: notification.read ? 'transparent' : isDark ? '#1a1a1a' : '#f6f8fa',
                    padding: '12px',
                    borderRadius: 8,
                    marginBottom: 8,
                  }}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <Text strong={!notification.read}>{notification.title}</Text>
                        <Tag color={typeColors[notification.type]} style={{ marginLeft: 8 }}>
                          {notification.type}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={4}>
                        <Text type="secondary">{notification.message}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {notification.time}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button type="link" onClick={() => navigate('/app/alerts')}>
            View All Alerts
          </Button>
        </div>
      </Drawer>
    </ProLayout>
  );
}
